/** "Needs your decision": one card per decision, plain words, Approve or Decline. */
import React, { useState } from "react";
import { useDecideCard, useMyDecisions } from "@/services/python-api/customerHooks";
import { Avatar, Card, Empty, Loading, Page, Pill, day } from "./parts";

function DecisionCard({ card, canApprove, approvers = [] }) {
  const decide = useDecideCard();
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const error = decide.error?.response?.data?.detail;
  const answer = (action) => decide.mutate({ id: card.id, action, version: card.version, note: note || null });
  return (
    <Card>
      <p className="text-[13px] font-medium text-[#667185]">Trucking price · …{card.car}</p>
      <p className="mt-1 text-[16px] font-semibold leading-snug text-[#101928]">{card.question}</p>
      <div className="mt-3 divide-y divide-[#F0F2F5] rounded-xl bg-[#F9FAFB] px-3">
        {card.facts.map((f) => (
          <div key={f.label} className="flex justify-between py-2 text-[14px]">
            <span className="text-[#667185]">{f.label}</span>
            <span className="font-medium text-[#1D2739]">{f.value}</span>
          </div>
        ))}
      </div>
      {canApprove ? (
        <>
          {showNote ? (
            <textarea
              className="mt-3 w-full rounded-xl border border-[#D0D5DD] p-3 text-[14px] outline-none focus:border-[#2563EB]"
              rows={2}
              maxLength={500}
              placeholder="Add a note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          ) : (
            <button className="mt-3 text-[13px] text-[#2563EB]" onClick={() => setShowNote(true)}>
              Add a note
            </button>
          )}
          <div className="mt-3 flex gap-2">
            <button
              disabled={decide.isPending}
              onClick={() => answer("approve")}
              className="h-11 flex-1 rounded-full bg-[#2563EB] text-[15px] font-medium text-white disabled:opacity-60"
            >
              Approve
            </button>
            <button
              disabled={decide.isPending}
              onClick={() => answer("decline")}
              className="h-11 flex-1 rounded-full border border-[#D0D5DD] bg-white text-[15px] font-medium text-[#344054] disabled:opacity-60"
            >
              Decline
            </button>
          </div>
          {error ? <p className="mt-2 text-[13px] text-[#CB1A14]">{error}</p> : null}
        </>
      ) : (
        <div className="mt-3 flex items-center gap-2 rounded-xl bg-[#F9FAFB] px-3 py-2.5">
          <div className="flex -space-x-2">
            {approvers.slice(0, 3).map((n) => (
              <span key={n} className="rounded-full ring-2 ring-[#F9FAFB]">
                <Avatar name={n} size={24} />
              </span>
            ))}
          </div>
          <p className="text-[13px] text-[#475367]">
            Waiting for {approvers.length ? approvers.join(", ") : "an approver"} to answer
          </p>
        </div>
      )}
    </Card>
  );
}

export default function MyDecisions() {
  const { data, isLoading } = useMyDecisions();
  const open = data?.open ?? [];
  const decided = data?.decided ?? [];
  return (
    <Page title="Needs your decision">
      {isLoading ? (
        <Loading />
      ) : (
        <div className="space-y-3">
          {open.length === 0 ? <Empty title="Nothing waiting for you">We'll ask here when a car needs your say.</Empty> : null}
          {open.map((card) => (
            <DecisionCard key={card.id} card={card} canApprove={data.can_approve} approvers={data.approvers} />
          ))}
          {decided.length ? <p className="px-1 pt-4 text-[13px] font-medium text-[#667185]">Answered</p> : null}
          {decided.map((card) => (
            <Card key={card.id} className="bg-[#F9FAFB]">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[14px] text-[#344054]">{card.question}</p>
                <Pill tone={card.decided.outcome === "approved" ? "bg-[#E7F6EC] text-[#036B26]" : "bg-[#F0F2F5] text-[#344054]"}>
                  {card.decided.outcome === "approved" ? "Approved" : "Declined"}
                </Pill>
              </div>
              <p className="mt-1 text-[12px] text-[#667185]">
                {card.decided.by} · {card.decided.where} · {day(card.decided.at)}
                {card.decided.note ? ` · "${card.decided.note}"` : ""}
              </p>
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}
